import { useCallback } from 'react';
import { mapUrlParamsToApi } from '../../utils';
import { useBaseQuerySync } from './useBaseQuerySync';
import type { InferParamsType, QueryParamConfigMap } from './useUrlQuerySync';

/**
 * Convert UI params to API params - maps to FilterParams structure
 */
function createMapToApiParams<TConfig extends QueryParamConfigMap, TParams extends InferParamsType<TConfig>, TApiParams>(config: TConfig) {
    return (params: TParams): TApiParams => {
        return mapUrlParamsToApi(params, config) as TApiParams;
    };
}

/**
 * Generic hook for managing query parameters with URL synchronization
 * This hook can be used for any entity that needs URL-synchronized filtering and pagination
 */
function useQuerySync<TConfig extends QueryParamConfigMap, TParams extends InferParamsType<TConfig>, TApiParams>({
    config,
    mapToApiParams,
    debounceMs = 300,
}: {
    config: TConfig;
    mapToApiParams: (params: TParams) => TApiParams;
    debounceMs?: number;
}) {
    // Memoize the mapping function to prevent unnecessary re-renders
    const mapToApiParamsMemo = useCallback(
        (params: TParams) => {
            return mapToApiParams(params);
        },
        [mapToApiParams],
    );

    return useBaseQuerySync({
        config,
        mapToApiParams: mapToApiParamsMemo,
        debounceMs,
    });
}

/**
 * Helper function to create a query sync hook with standard pagination
 */
export function createQuerySyncHook<TConfig extends QueryParamConfigMap, TParams extends InferParamsType<TConfig>, TApiParams>(
    config: TConfig,
    debounceMs = 300,
) {
    const mapToApiParams = createMapToApiParams<TConfig, TParams, TApiParams>(config);

    return () =>
        useQuerySync({
            config,
            mapToApiParams,
            debounceMs,
        });
}
